import {UseFormReturn} from 'react-hook-form';

import {indexBy} from 'ramda';

import {RentalOrderResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {useVatRatesOptions} from '@dms/shared';

import {Nullish} from 'shared/web';

import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

type HandleUpdateChargedKilometersProps = {
  formApi: UseFormReturn<ReturnVehicleFormValues>;
  rentalOrder: RentalOrderResponseBody | Nullish;
};

export const useGetHandleUpdateChargedKilometers = (props: HandleUpdateChargedKilometersProps) => {
  const [vatOptions] = useVatRatesOptions();
  const vatRatesByCode = indexBy((vat) => vat.value, vatOptions);

  const handleUpdateChargedKilometers = (
    returnMilage: number | Nullish,
    totalFreeKilometers: number | Nullish
  ) => {
    const [pricePerKilometer, chargedKilometersVatType] = props.formApi.getValues([
      'rentPrice.pricePerKilometer.withoutVat',
      'rentPrice.chargedKilometer.price.vatType',
    ]);
    const vatRate = Number(vatRatesByCode[chargedKilometersVatType ?? '']?.rate ?? '0');
    const milageWhenRented = props.rentalOrder?.rentVehicle?.actualMileage ?? 0;
    const milageWhenReturning = returnMilage ?? 0;

    if (milageWhenRented > milageWhenReturning) {
      props.formApi.setError(
        'actualMileage',
        {
          type: 'manual',
          message: i18n.t('page.rentalOrder.errors.actualMileageIsLowerThanRentedMileage'),
        },
        {shouldFocus: true}
      );
      return;
    } else {
      props.formApi.clearErrors('actualMileage');
    }

    const chargedKilometers = milageWhenReturning - milageWhenRented - (totalFreeKilometers ?? 0);

    if (chargedKilometers <= 0) {
      props.formApi.setValue('rentPrice.chargedKilometer.amount', 0);
      props.formApi.setValue('rentPrice.chargedKilometer.price.withoutVat', 0);
      props.formApi.setValue('rentPrice.chargedKilometer.price.vat', 0);
      props.formApi.setValue('rentPrice.chargedKilometer.price.withVat', 0);
      return;
    }

    const chargedMilagePriceWithoutVat = chargedKilometers * (pricePerKilometer ?? 0);
    const chargedMilagePriceWithVat = chargedMilagePriceWithoutVat * (1 + (vatRate ?? 0) / 100);
    const chargedMilageVat = chargedMilagePriceWithVat - chargedMilagePriceWithoutVat;

    props.formApi.setValue('rentPrice.chargedKilometer.amount', chargedKilometers);
    props.formApi.setValue(
      'rentPrice.chargedKilometer.price.withoutVat',
      chargedMilagePriceWithoutVat
    );
    props.formApi.setValue('rentPrice.chargedKilometer.price.vat', chargedMilageVat);
    props.formApi.setValue('rentPrice.chargedKilometer.price.withVat', chargedMilagePriceWithVat);
  };

  return [handleUpdateChargedKilometers] as const;
};
